import { FlexColumn, LabeledInput, useLoading } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, REQUIRED_FIELD_TEXT } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useForms } from '../../hooks/useForms';
import { useStyles } from '../../hooks/useStyles';
import { createCompany, updateCompany } from '../../services/companiesService';
import {
  editCompanyErrorState,
  editCompanyState,
  editCompanyTouchedState,
  editCompanyValidState,
} from '../../storage/companiesStates';
import { Company, CompanyRequest, CompanyRequestTouched } from '../../types/companies';
import { FormActionsRow } from '../shared/FormActionsRow';
import StyledModal from '../shared/StyledModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string;
  company?: Company;
  onSubmitFinished?: () => void;
};

export function CompanyModal({ isOpen, onClose, companyId, company, onSubmitFinished }: Readonly<Props>) {
  const editMode = companyId !== undefined && company !== undefined;
  const { css } = useStyles();
  const [form, setForm] = useRecoilState(editCompanyState);
  const [formTouched, setFormTouched] = useRecoilState(editCompanyTouchedState);
  const formHasError = useRecoilValue(editCompanyErrorState);
  const resetEditCompanyState = useResetRecoilState(editCompanyState);
  const resetEditCompanyTouchedState = useResetRecoilState(editCompanyTouchedState);
  const formValid = useRecoilValue(editCompanyValidState);
  const { loading, startLoading, stopLoading } = useLoading();
  const canSubmit = !formHasError;
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { onTouched } = useForms<CompanyRequest, CompanyRequestTouched>(setForm, setFormTouched);

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!company) return;
    setForm(company);
  }, [company]);

  const clearStates = () => {
    resetEditCompanyState();
    resetEditCompanyTouchedState();
  };

  const onChangeInput = (key: keyof CompanyRequest, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onSubmit = async () => {
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      clearStates();
      onSubmitFinished && onSubmitFinished();
    };
    startLoading();
    if (editMode) {
      const { error } = await updateCompany(companyId, form);
      if (!error) {
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.company, form.name));
      } else {
        showFailFeedback(error);
      }
    } else {
      const { error } = await createCompany(form);
      if (!error) {
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.company));
      } else {
        showFailFeedback(error);
      }
    }
    stopLoading();
  };

  const onCloseModal = () => {
    clearStates();
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title={editMode ? 'Editar ' : 'Nueva '}
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '525px',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            <LabeledInput
              label="Nombre"
              placeholder="Ingresa nombre"
              value={form.name}
              onChange={(value) => {
                onChangeInput('name', value);
              }}
              onBlur={() => onTouched('nameTouched')}
              error={formTouched.nameTouched && !formValid.name && REQUIRED_FIELD_TEXT}
              required
            />
            <LabeledInput
              label="Razón social"
              placeholder="Ingresa la razón social"
              value={form.socialReason}
              onChange={(value) => {
                onChangeInput('socialReason', value);
              }}
            />
            {editMode && (
              <Checkbox
                checked={form.active}
                onChange={(e) => onChangeInput('active', e.target.checked)}
              >
                Activo
              </Checkbox>
            )}
          </FlexColumn>
          <FormActionsRow
            canSubmit={canSubmit}
            loading={loading}
            editMode={editMode}
            onSubmit={onSubmit}
            onCancel={onCloseModal}
          />
        </FlexColumn>
      }
    />
  );
}
